import { useEffect, useState } from "react";
import { MonitorDown, Share, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY = "rc-install-prompt-v1";
let deferred = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });
}

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const platform = () => {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (ios) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Firefox/i.test(ua)) return "firefox";
  if (/Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)) return "mac-safari";
  return "desktop";
};

const STEPS = {
  ios: ["Tap the Share button at the bottom of Safari", "Scroll down and tap \u201cAdd to Home Screen\u201d", "Tap \u201cAdd\u201d, and The Roots Club appears on your home screen"],
  android: ["Tap the \u22ee menu at the top right of Chrome", "Tap \u201cAdd to Home screen\u201d or \u201cInstall app\u201d", "Tap \u201cInstall\u201d, and The Roots Club appears on your home screen"],
  "mac-safari": ["In Safari's menu bar, click File", "Click \u201cAdd to Dock\u201d", "Click \u201cAdd\u201d, and The Roots Club opens from your Dock like an app"],
  desktop: ["Click the install icon at the right of the address bar (a screen with a down arrow)", "Or open the browser menu and choose \u201cInstall The Roots Club\u201d", "It then opens in its own window from your taskbar or dock"],
  firefox: ["Press Ctrl+D (or Cmd+D on Mac) to bookmark The Roots Club", "Or open it in Chrome, Edge or Safari to install it as an app"],
};

export const isInstalled = standalone;

export const InstallPrompt = ({ auto, open, setOpen }) => {
  const [p] = useState(platform);
  const [, force] = useState(0);
  useEffect(() => {
    if (!auto || standalone() || localStorage.getItem(KEY)) return undefined;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [auto, setOpen]);
  useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (!open || standalone()) return null;
  const close = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const install = async () => {
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    deferred = null;
    close();
  };
  const mobile = p === "ios" || p === "android";
  const Icon = mobile ? Smartphone : MonitorDown;
  return (
    <div role="dialog" aria-label="Add The Roots Club to your home screen" data-testid="install-prompt"
      className="fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] rounded-3xl bg-navy text-white p-6 shadow-2xl fade-up">
      <button onClick={close} aria-label="Close" data-testid="install-prompt-close" className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
      <div className="flex items-center gap-3 pr-6">
        <img src="/brand/icon-192.png" alt="" className="h-12 w-12 rounded-xl" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-sun flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{mobile ? "Add to your home screen" : "Install the app"}</div>
          <p className="font-serif text-lg leading-tight mt-0.5">For the best experience</p>
        </div>
      </div>
      <p className="text-sm text-white/80 mt-3">{mobile ? "Add to your home screen for the best experience" : "Install it on your computer for the best experience"}: one tap to open, full screen, no browser bar.</p>
      {deferred ? (
        <Button onClick={install} data-testid="install-prompt-install" className="mt-4 w-full rounded-full bg-pinkb hover:bg-pinkb/90 text-white">{mobile ? "Add to home screen" : "Install the app"}</Button>
      ) : (
        <ol className="mt-4 space-y-2 text-sm" data-testid={`install-steps-${p}`}>{STEPS[p].map((s, i) => (
          <li key={s} className="flex gap-3"><span className="font-serif text-sun w-4 shrink-0">{i + 1}</span><span>{s}{p === "ios" && i === 0 && <Share className="inline h-4 w-4 ml-1 -mt-0.5 text-sun" />}</span></li>
        ))}</ol>
      )}
      <button onClick={close} data-testid="install-prompt-later" className="mt-4 text-xs text-white/60 hover:text-white">{deferred ? "Not now" : "Got it"}</button>
    </div>
  );
};
